import { AlertColor, Box, Grid } from "@mui/material";
import Iconborder from "../IconBorder";
import { ArrowBackIos, ArrowForwardIos, Key } from "@mui/icons-material";
import { KeyDriveh4Header } from "../../../../../components/KeydriveComponents/Fonts/Headers/KeyDriveh4Header";
import { useTranslation } from "react-i18next";
import { KeyDriveBodyMin } from "../../../../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMin";
import { KeydriveTextfield } from "../../../../../components/KeydriveComponents/Textfield/KeydriveTextfield";
import { IRegisterUserObject } from "../../Register";
import { KeyDrivePrimaryButton } from "../../../../../components/KeydriveComponents/Buttons/KeyDrivePrimaryButton";
import { KeyDriveBodyMax } from "../../../../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMax";
import { KeyDriveSecondaryButton } from "../../../../../components/KeydriveComponents/Buttons/KeyDriveSecondaryButton";
import { useEffect, useState } from "react";
import RegisterPasswordRule from "../RegisterPasswordRule";
import ForwardBackButton from "../ForwardBackButton";

interface IRegisterPassword {
    createUserObject: IRegisterUserObject;
    passwordHandler: (input: string) => void;
    confirmPasswordHandler: (input: string) => void;
    forwardStep: () => void;
    backStep: () => void;
    setAlertColor: (set: AlertColor) => void;
    setMessage: (set: string) => void;
}

interface IPasswordRule {
    passwordContent: string;
    isRuleFullfiled: boolean;
}

export const REGEX_MIN_LENGTH = /^.{8,}$/;
export const REGEX_SPECIAL_CHAR = /[!@#$%^&*(),.?":{}|<>]/;
export const REGEX_NUMBER = /[0-9]/;
export const REGEX_UPPERCASE = /[A-Z]/;

export default function RegisterPassword({
    createUserObject,
    confirmPasswordHandler,
    passwordHandler,
    backStep,
    forwardStep,
    setAlertColor,
    setMessage
}: IRegisterPassword): JSX.Element {

    const { t } = useTranslation();

    const [passwordRules, setPasswordRules] = useState<IPasswordRule[]>([
        {
            isRuleFullfiled: false,
            passwordContent: t("Register.Minimun8")
        },
        {
            isRuleFullfiled: false,
            passwordContent: t("Register.AnUpperCaseLetter")
        },
        {
            isRuleFullfiled: false,
            passwordContent:  t("Register.ASpecialLetter")
        },
        {
            isRuleFullfiled: false,
            passwordContent:  t("Register.Numeric")
        },
        {
            isRuleFullfiled: false,
            passwordContent:  t("Register.PasswordMatch")
        },
    ]);

    useEffect(() => {
        const newRules: IPasswordRule[] = [
            {
                isRuleFullfiled: REGEX_MIN_LENGTH.test(createUserObject.password),
                passwordContent:  t("Register.Minimun8")
            },
            {
                isRuleFullfiled: REGEX_UPPERCASE.test(createUserObject.password),
                passwordContent: t("Register.AnUpperCaseLetter")
            },
            {
                isRuleFullfiled: REGEX_SPECIAL_CHAR.test(createUserObject.password),
                passwordContent: t("Register.ASpecialLetter")
            },
            {
                isRuleFullfiled: REGEX_NUMBER.test(createUserObject.password),
                passwordContent: t("Register.Numeric")
            },
            {
                isRuleFullfiled:
                    createUserObject?.password?.length > 0 &&
                    createUserObject?.password === createUserObject.confirmPassword,
                passwordContent: t("Register.PasswordMatch")
            }
        ];

        setPasswordRules(newRules);
    }, [createUserObject.password, createUserObject.confirmPassword]);

    return (
        <>
            <Grid item xs={12} display="flex" justifyContent="center" marginBottom="12px">
                <Iconborder>
                    <Key sx={{ color: "#22638D", width: "36px", height: "36px" }} />
                </Iconborder>
            </Grid>
            <Grid item xs={12} textAlign="center" marginBottom="32px">
                <KeyDriveh4Header
                    color={"#22638D"}
                    needBold
                    needEllipses={false}
                    text={t("RegisterPage.steps.step2.title")}
                    sxStyles={{
                        marginBottom: "12px"
                    }}
                />
                <KeyDriveBodyMin
                    color="#7AA1BB"
                    needBold={false}
                    needEllipses={false}
                    text={t("RegisterPage.steps.step2.content")}
                />
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="space-between" marginTop="12px">
                <KeydriveTextfield 
                    label={t("Register.Password")}
                    onChange={e => passwordHandler(e.target.value)}
                    value={createUserObject.password}
                    placeholder={t("Register.TypeHere")}
                    needFullWidth
                    type="password"
                    required
                />
            </Grid>
            <Grid item xs={12} textAlign="center" marginTop="40px">
                <KeydriveTextfield 
                    label={t("Register.ConfirmPassword")}
                    onChange={e => confirmPasswordHandler(e.target.value)}
                    value={createUserObject.confirmPassword}
                    placeholder={t("Register.TypeHere")}
                    needFullWidth
                    type="password"
                    required
                />
            </Grid>
            <Grid item xs={12} display="flex" flexDirection="column" justifyContent="center" marginTop="20px">
                { passwordRules.map(({ isRuleFullfiled, passwordContent }) => (
                    <RegisterPasswordRule 
                        isRuleFulfilled={isRuleFullfiled}
                        ruleContent={passwordContent}
                    />
                )) }
            </Grid>
            <ForwardBackButton 
                backStep={backStep}
                forwardStep={() => {
                    if (createUserObject.confirmPassword == "" || createUserObject.password == "") {
                        setMessage(t("Register.PassNull"));
                        setAlertColor("error");
                        throw new Error();
                    }
                    forwardStep();
                }}
            />
        </>
    )
}